import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { RFI_STATE_LABELS } from "./RfiPanel";

export default function RfiDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/rfis/${id}`).then((x) => setR(x.data))
    .catch((e) => setError(errMsg(e, "Could not load this RFI."))), [id]);
  useEffect(() => { load(); }, [load]);
  const act = (path, body) => api.post(`/phase3/rfis/${id}/${path}`, body)
    .then(() => { setText(""); setError(null); load(); })
    .catch((e) => setError(errMsg(e, "That action did not work.")));
  const manager = r?.role === "manager";
  return (
    <AdminAppShell activeNav="architect" user={user} title={r ? `${r.number} · ${r.subject}` : "RFI"}
      subtitle={r ? RFI_STATE_LABELS[r.state] : ""}
      breadcrumbs={[{ label: "Architect Workspace", to: "/phase3/architect" }, { label: r?.number || "RFI", to: null }]}>
      <div data-testid="phase3-rfi-detail">
        {error && <p role="alert" className="mb-3 text-sm text-[#991B1B]" data-testid="phase3-rfi-detail-error">{error}</p>}
        {r && (
          <Card className="ds-card border-erf-borderCream">
            <CardContent className="p-5 space-y-3">
              <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">Question</h2>
              <p className="text-sm font-nav text-erf-dark whitespace-pre-wrap" data-testid="phase3-rfi-question-text">{r.question}</p>
              <p className="text-xs font-nav text-erf-dark/80">
                {r.spec_section && <>Spec section {r.spec_section} · </>}{r.due_date ? `Answer needed by ${r.due_date}` : "No due date"}
              </p>
              <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">Answer</h2>
              <p className="text-sm font-nav text-erf-dark whitespace-pre-wrap" data-testid="phase3-rfi-answer-text">{r.answer || "Not answered yet."}</p>
              {(r.state === "open" || manager) && r.state !== "closed" && r.state !== "void" && (
                <label className="block text-sm font-nav text-erf-dark">
                  {r.state === "open" ? "Your answer" : "Reason (at least 10 characters)"}
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} className="mt-1" data-testid="phase3-rfi-text" />
                </label>
              )}
              <div className="flex flex-wrap gap-2">
                {r.state === "open" && <Button className="h-11 bg-erf-dark text-[#FEFCF7]" onClick={() => act("answer", { answer: text })} data-testid="phase3-rfi-answer">Send answer</Button>}
                {manager && r.state === "answered" && <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => act("close")} data-testid="phase3-rfi-close">Close RFI</Button>}
                {manager && r.state === "answered" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => act("reopen", { reason: text })} data-testid="phase3-rfi-reopen">Reopen</Button>}
                {manager && (r.state === "open" || r.state === "answered") && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => act("void", { reason: text })} data-testid="phase3-rfi-void">Void</Button>}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminAppShell>
  );
}
